import {gql} from '@apollo/client';

import {AaNaturalAnalog} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import staticURL from 'lib/staticURL';
import {AMINO_ACID_COLORINGS, ProteinColorScheme} from 'molbio/aa-sequences/util/AaSequenceColoringConstants';
import sortedBy from 'util/sortedBy';

import {AaMonomerQuery} from './__generated/AaMonomerQuery';

export const AA_MONOMER_LIBRARY_URL = '/aa-monomers/listing';

export const DEFAULT_AA_MONOMER_COLOR = '#808080';

export const RECOMMENDED_AA_MONOMER_COLORS: Array<string> = Array.from(
  new Set(
    Object.values(AMINO_ACID_COLORINGS[ProteinColorScheme.RASMOL]).map(({backgroundColor}) =>
      backgroundColor.toUpperCase()
    )
  )
);

// Keep in sync with RASMOL_COLORS in benchling/molbio/aa_monomers/data_helpers.py
const RASMOL_COLORS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(AMINO_ACID_COLORINGS[ProteinColorScheme.RASMOL]).map(([symbol, {backgroundColor}]) => [
      symbol,
      backgroundColor,
    ])
  ),
  // Degenerate and non-canonical analogs are not part of the standard RasMol scheme; render white.
  B: '#FFFFFF',
  J: '#FFFFFF',
  O: '#FFFFFF',
  U: '#FFFFFF',
  X: '#FFFFFF',
  Z: '#FFFFFF',
};

// The default visual color for a monomer with the given natural analog, matching the HELM import.
export function getDefaultAaMonomerColor(naturalAnalog: AaNaturalAnalog): string {
  return RASMOL_COLORS[naturalAnalog] ?? DEFAULT_AA_MONOMER_COLOR;
}

export const AA_HELM_LIBRARY_LINK = staticURL('/static/aa_monomers/HELMCoreLibrary.json');

type AaMonomerNode = AaMonomerQuery['aaMonomers'][number];

// An entry from the AA HELM Core Monomer library JSON. It shares the amino-acid identity fields
// with a queried AA monomer, plus the raw import `smiles` that only exists in the static library.
export type AaMonomerGraphQL = Pick<AaMonomerNode, 'name' | 'symbol' | 'naturalAnalog'> & {
  smiles: string;
};

export const AAHELMCoreMonomerLibraryAPI = {
  fetch: async (): Promise<Array<AaMonomerGraphQL>> => {
    const resp = await fetch(AA_HELM_LIBRARY_LINK);
    return (await resp.json()) as Array<AaMonomerGraphQL>;
  },
};

export const AA_MONOMER_QUERY = gql`
  query AaMonomerQuery @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    aaMonomers {
      id
      name
      symbol
      naturalAnalog
      isNatural
      canonicalSmiles
      originalSmiles
      visualColor
      calculatedMolecularWeight
      exactMolecularWeight
      customMolecularWeight
      chemicalStructureImage {
        ... on SVGNode {
          base64Svg
        }
        ... on ErrorNode {
          message
        }
      }
    }
    currentTenant {
      id
      permissions {
        canCreateAaMonomers
      }
    }
  }
`;

export const AA_MONOMER_BY_ID = gql`
  query AaMonomerByIdQuery($id: StrictString!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    aaMonomer(id: $id) {
      ... on AaMonomerNode {
        id
        name
        symbol
        naturalAnalog
        isNatural
        canonicalSmiles
        originalSmiles
        visualColor
        calculatedMolecularWeight
        exactMolecularWeight
        customMolecularWeight
        chemicalStructureImage {
          ... on SVGNode {
            base64Svg
          }
          ... on ErrorNode {
            message
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const AA_NATURAL_ANALOG_DISPLAY_NAME: Record<AaNaturalAnalog, string> = {
  [AaNaturalAnalog.A]: 'Alanine',
  [AaNaturalAnalog.B]: 'Aspartic acid or Asparagine',
  [AaNaturalAnalog.C]: 'Cysteine',
  [AaNaturalAnalog.D]: 'Aspartic acid',
  [AaNaturalAnalog.E]: 'Glutamic acid',
  [AaNaturalAnalog.F]: 'Phenylalanine',
  [AaNaturalAnalog.G]: 'Glycine',
  [AaNaturalAnalog.H]: 'Histidine',
  [AaNaturalAnalog.I]: 'Isoleucine',
  [AaNaturalAnalog.J]: 'Leucine or Isoleucine',
  [AaNaturalAnalog.K]: 'Lysine',
  [AaNaturalAnalog.L]: 'Leucine',
  [AaNaturalAnalog.M]: 'Methionine',
  [AaNaturalAnalog.N]: 'Asparagine',
  [AaNaturalAnalog.O]: 'Pyrrolysine',
  [AaNaturalAnalog.P]: 'Proline',
  [AaNaturalAnalog.Q]: 'Glutamine',
  [AaNaturalAnalog.R]: 'Arginine',
  [AaNaturalAnalog.S]: 'Serine',
  [AaNaturalAnalog.T]: 'Threonine',
  [AaNaturalAnalog.U]: 'Selenocysteine',
  [AaNaturalAnalog.V]: 'Valine',
  [AaNaturalAnalog.W]: 'Tryptophan',
  [AaNaturalAnalog.X]: 'Any amino acid',
  [AaNaturalAnalog.Y]: 'Tyrosine',
  [AaNaturalAnalog.Z]: 'Glutamic acid or Glutamine',
};

export const AA_NATURAL_ANALOG_FILTER_OPTIONS = [
  {name: 'Any natural analog', value: [] as Array<AaNaturalAnalog>},
  ...sortedBy(Object.values(AaNaturalAnalog) as Array<AaNaturalAnalog>, [(na) => na]).map((na) => ({
    name: AA_NATURAL_ANALOG_DISPLAY_NAME[na],
    value: [na],
  })),
];

// Single-select options (one analog per option) for the manual-creation define pane's analog dropdown.
export const AA_NATURAL_ANALOG_SELECT_OPTIONS = sortedBy(
  Object.values(AaNaturalAnalog) as Array<AaNaturalAnalog>,
  [(naturalAnalog) => naturalAnalog]
).map((naturalAnalog) => ({
  name: `${naturalAnalog} (${AA_NATURAL_ANALOG_DISPLAY_NAME[naturalAnalog]})`,
  value: naturalAnalog,
}));
